import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { TimelineButtons } from '../components/TimelineButtons';
import { toast } from 'sonner';
import { format, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday, addMonths, subMonths } from 'date-fns';
import { 
    Heart,
    Droplets,
    Calendar as CalendarIcon,
    ChevronLeft,
    ChevronRight,
    Plus,
    Sparkles,
    TrendingUp,
    Moon,
    Sun,
    Frown,
    Meh,
    Smile,
    Zap,
    CloudRain,
    Activity,
    X,
    Check,
    Info
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CYCLE_SYMPTOMS = [
    'Cramps', 'Bloating', 'Headache', 'Back Pain', 'Breast Tenderness',
    'Fatigue', 'Nausea', 'Acne', 'Cravings', 'Insomnia', 'Hot Flashes', 'Joint Pain'
];

const MOODS = [
    { value: 'happy', label: 'Happy', icon: Smile, color: 'text-emerald-500' },
    { value: 'calm', label: 'Calm', icon: Sun, color: 'text-blue-500' },
    { value: 'energetic', label: 'Energetic', icon: Zap, color: 'text-amber-500' },
    { value: 'anxious', label: 'Anxious', icon: Activity, color: 'text-orange-500' },
    { value: 'irritable', label: 'Irritable', icon: CloudRain, color: 'text-red-500' },
    { value: 'sad', label: 'Sad', icon: Frown, color: 'text-indigo-500' },
    { value: 'neutral', label: 'Neutral', icon: Meh, color: 'text-gray-500' },
];

const FLOW_INTENSITIES = [
    { value: 'spotting', label: 'Spotting', color: 'bg-pink-200' },
    { value: 'light', label: 'Light', color: 'bg-pink-300' },
    { value: 'medium', label: 'Medium', color: 'bg-pink-400' },
    { value: 'heavy', label: 'Heavy', color: 'bg-pink-500' },
];

export default function CycleTracker() {
    const { token } = useAuth();
    const [entries, setEntries] = useState([]);
    const [periods, setPeriods] = useState(null);
    const [insights, setInsights] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('calendar');
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);
    const [showEntryForm, setShowEntryForm] = useState(false);
    
    // Entry form state
    const [isPeriodDay, setIsPeriodDay] = useState(false);
    const [flowIntensity, setFlowIntensity] = useState('');
    const [selectedSymptoms, setSelectedSymptoms] = useState([]);
    const [symptomSeverities, setSymptomSeverities] = useState({});
    const [mood, setMood] = useState('');
    const [energyLevel, setEnergyLevel] = useState(5);
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchEntries = useCallback(async () => {
        try {
            console.log('[CycleTracker] Fetching cycle entries (90 days)...');
            const res = await fetch(`${API_URL}/api/cycles?days=90`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                console.log('[CycleTracker] Entries received:', data.length);
                console.log('[CycleTracker] Period days:', data.filter(e => e.is_period_day).length);
                console.log('[CycleTracker] Sample entries feeding into correlations:', data.slice(0, 3).map(e => ({ date: e.date, isPeriod: e.is_period_day, mood: e.mood })));
                setEntries(data || []);
            } else {
                console.error('[CycleTracker] API error:', res.status);
            }
        } catch (error) {
            console.error('[CycleTracker] Failed to fetch cycle entries:', error);
        }
    }, [token]);

    const fetchPeriods = useCallback(async () => {
        try {
            console.log('[CycleTracker] Fetching period analysis (6 months)...');
            const res = await fetch(`${API_URL}/api/cycles/periods?months=6`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                console.log('[CycleTracker] Period data received:', data);
                console.log('[CycleTracker] Average cycle length:', data.average_cycle_length, 'days');
                console.log('[CycleTracker] Next period prediction:', data.next_period_prediction);
                setPeriods(data);
            } else {
                console.error('[CycleTracker] Periods API error:', res.status);
            }
        } catch (error) {
            console.error('[CycleTracker] Failed to fetch periods:', error);
        }
    }, [token]);

    const fetchInsights = useCallback(async () => {
        try {
            console.log('[CycleTracker] Fetching cycle insights...');
            const res = await fetch(`${API_URL}/api/cycles/insights`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                console.log('[CycleTracker] Insights received:', data);
                console.log('[CycleTracker] Has sufficient data for correlations:', data.has_sufficient_data);
                if (data.period_specific_symptoms) {
                    console.log('[CycleTracker] Period-specific symptoms (correlation data):', data.period_specific_symptoms);
                }
                setInsights(data);
            } else {
                console.error('[CycleTracker] Insights API error:', res.status);
            }
        } catch (error) {
            console.error('[CycleTracker] Failed to fetch insights:', error);
        }
    }, [token]);

    useEffect(() => {
        const loadData = async () => {
            setLoading(true);
            await Promise.all([fetchEntries(), fetchPeriods(), fetchInsights()]);
            setLoading(false);
        };
        loadData();
    }, [fetchEntries, fetchPeriods, fetchInsights]);

    const entriesByDate = entries.reduce((acc, entry) => {
        acc[entry.date] = entry;
        return acc;
    }, {});

    const handleDateClick = (date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        setSelectedDate(dateStr);
        
        const existingEntry = entriesByDate[dateStr];
        if (existingEntry) {
            setIsPeriodDay(existingEntry.is_period_day || false);
            setFlowIntensity(existingEntry.flow_intensity || '');
            setSelectedSymptoms(existingEntry.symptoms?.map(s => s.name) || []);
            setSymptomSeverities(
                existingEntry.symptoms?.reduce((acc, s) => ({ ...acc, [s.name]: s.severity }), {}) || {}
            );
            setMood(existingEntry.mood || '');
            setEnergyLevel(existingEntry.energy_level || 5);
            setNotes(existingEntry.notes || '');
        } else {
            setIsPeriodDay(false);
            setFlowIntensity('');
            setSelectedSymptoms([]);
            setSymptomSeverities({});
            setMood('');
            setEnergyLevel(5);
            setNotes('');
        }
        setShowEntryForm(true);
    };

    const handleSaveEntry = async () => {
        if (!selectedDate) return;
        
        setSaving(true);
        try {
            const entry = {
                date: selectedDate,
                is_period_day: isPeriodDay,
                flow_intensity: isPeriodDay ? flowIntensity : null,
                symptoms: selectedSymptoms.map(name => ({
                    name,
                    severity: symptomSeverities[name] || 5
                })),
                mood: mood || null,
                energy_level: energyLevel,
                notes: notes || null
            };

            const res = await fetch(`${API_URL}/api/cycles`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(entry)
            });

            if (res.ok) {
                toast.success('Entry saved');
                setShowEntryForm(false);
                fetchEntries();
                fetchPeriods();
            } else {
                toast.error('Failed to save entry');
            }
        } catch (error) {
            toast.error('Failed to save entry');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteEntry = async () => {
        if (!selectedDate) return;
        
        try {
            const res = await fetch(`${API_URL}/api/cycles/${selectedDate}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (res.ok) {
                toast.success('Entry deleted');
                setShowEntryForm(false);
                fetchEntries();
                fetchPeriods();
            }
        } catch (error) {
            toast.error('Failed to delete entry');
        }
    };

    const toggleSymptom = (symptom) => {
        if (selectedSymptoms.includes(symptom)) {
            setSelectedSymptoms(prev => prev.filter(s => s !== symptom));
        } else {
            setSelectedSymptoms(prev => [...prev, symptom]);
            if (!symptomSeverities[symptom]) {
                setSymptomSeverities(prev => ({ ...prev, [symptom]: 5 }));
            }
        }
    };

    // Calendar rendering
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const calendarDays = eachDayOfInterval({ start: monthStart, end: monthEnd });
    
    // Pad to start on Sunday
    const startDay = monthStart.getDay();
    const paddedDays = Array(startDay).fill(null).concat(calendarDays);

    const renderCalendarDay = (date) => {
        if (!date) return <div className="h-12 md:h-16" />;
        
        const dateStr = format(date, 'yyyy-MM-dd');
        const entry = entriesByDate[dateStr];
        const isPeriod = entry?.is_period_day;
        const hasSymptoms = entry?.symptoms?.length > 0;
        const isCurrentMonth = isSameMonth(date, currentMonth);
        const isPredicted = periods?.next_period_prediction && 
            dateStr >= periods.next_period_prediction && 
            dateStr <= format(addMonths(parseISO(periods.next_period_prediction), 0), 'yyyy-MM-dd');

        return (
            <button
                onClick={() => handleDateClick(date)}
                className={`
                    h-12 md:h-16 w-full rounded-lg flex flex-col items-center justify-center gap-0.5 transition-all
                    ${!isCurrentMonth ? 'opacity-30' : ''}
                    ${isToday(date) ? 'ring-2 ring-primary' : ''}
                    ${isPeriod ? 'bg-pink-100 dark:bg-pink-900/40' : 'hover:bg-muted'}
                    ${isPredicted && !isPeriod ? 'bg-pink-50 dark:bg-pink-950/30 border border-dashed border-pink-300' : ''}
                `}
                data-testid={`calendar-day-${dateStr}`}
            >
                <span className={`text-sm font-medium ${isToday(date) ? 'text-primary' : ''}`}>
                    {format(date, 'd')}
                </span>
                <div className="flex gap-0.5">
                    {isPeriod && (
                        <Droplets className="w-3 h-3 text-pink-500" />
                    )}
                    {hasSymptoms && !isPeriod && (
                        <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    )}
                    {entry?.mood && (
                        <div className="w-1.5 h-1.5 rounded-full bg-indigo-400" />
                    )}
                </div>
            </button>
        );
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-3xl font-bold flex items-center gap-3">
                        <Heart className="w-8 h-8 text-pink-500" />
                        Cycle Tracker
                    </h1>
                    <p className="text-muted-foreground mt-1">Track your menstrual cycle and related symptoms</p>
                </div>
                <TimelineButtons />
            </div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                {periods?.next_period_prediction && (
                    <Card className="bg-gradient-to-r from-pink-50 to-purple-50 dark:from-pink-950/30 dark:to-purple-950/30 border-pink-200 dark:border-pink-800">
                        <CardContent className="p-4">
                            <p className="text-sm text-muted-foreground">Next period predicted</p>
                            <p className="text-lg font-semibold text-pink-600 dark:text-pink-400">
                                {format(parseISO(periods.next_period_prediction), 'MMMM d, yyyy')}
                            </p>
                        </CardContent>
                    </Card>
                )}
            </div>

            {/* Stats Summary */}
            {periods && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-pink-500">{periods.average_cycle_length || '-'}</p>
                            <p className="text-sm text-muted-foreground">Avg Cycle Length</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-purple-500">{periods.average_period_length || '-'}</p>
                            <p className="text-sm text-muted-foreground">Avg Period Length</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-indigo-500">{periods.periods?.length || 0}</p>
                            <p className="text-sm text-muted-foreground">Periods Tracked</p>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4 text-center">
                            <p className="text-2xl font-bold text-amber-500">{entries.length}</p>
                            <p className="text-sm text-muted-foreground">Days Logged</p>
                        </CardContent>
                    </Card>
                </div>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList>
                    <TabsTrigger value="calendar">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        Calendar
                    </TabsTrigger>
                    <TabsTrigger value="insights">
                        <Sparkles className="w-4 h-4 mr-2" />
                        Insights
                    </TabsTrigger>
                    <TabsTrigger value="history">
                        <TrendingUp className="w-4 h-4 mr-2" />
                        History
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="calendar" className="mt-6">
                    <Card>
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
                                >
                                    <ChevronLeft className="w-5 h-5" />
                                </Button>
                                <h2 className="text-lg font-semibold">
                                    {format(currentMonth, 'MMMM yyyy')}
                                </h2>
                                <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
                                >
                                    <ChevronRight className="w-5 h-5" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent>
                            {/* Day headers */}
                            <div className="grid grid-cols-7 gap-1 mb-2">
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                                    <div key={day} className="text-center text-xs font-medium text-muted-foreground py-2">
                                        {day}
                                    </div>
                                ))}
                            </div>
                            {/* Calendar grid */}
                            <div className="grid grid-cols-7 gap-1">
                                {paddedDays.map((date, i) => (
                                    <div key={i}>
                                        {renderCalendarDay(date)}
                                    </div>
                                ))}
                            </div>
                            {/* Legend */}
                            <div className="flex flex-wrap gap-4 mt-4 pt-4 border-t text-xs text-muted-foreground">
                                <div className="flex items-center gap-1">
                                    <Droplets className="w-3 h-3 text-pink-500" />
                                    <span>Period day</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <div className="w-2 h-2 rounded-full bg-amber-400" />
                                    <span>Symptoms logged</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <div className="w-6 h-4 rounded border border-dashed border-pink-300 bg-pink-50 dark:bg-pink-950/30" />
                                    <span>Predicted</span>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                <TabsContent value="insights" className="mt-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-600"></div>
                        </div>
                    ) : !insights?.has_sufficient_data ? (
                        <Card>
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <Info className="w-12 h-12 text-muted-foreground/50 mb-4" />
                                <h3 className="font-medium text-lg mb-2">Keep Tracking!</h3>
                                <p className="text-muted-foreground text-center max-w-md">
                                    {insights?.message || 'Track at least 2 weeks of data to see personalized insights about your cycle patterns.'}
                                </p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="grid md:grid-cols-2 gap-6">
                            {insights.period_specific_symptoms?.length > 0 && (
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="text-base flex items-center gap-2">
                                            <Activity className="w-5 h-5 text-pink-500" />
                                            Period-Specific Symptoms
                                        </CardTitle>
                                        <CardDescription>
                                            Symptoms that occur more during your period
                                        </CardDescription>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="space-y-3">
                                            {insights.period_specific_symptoms.map((sym, idx) => (
                                                <div key={idx} className="flex items-center justify-between">
                                                    <span className="font-medium">{sym.name}</span>
                                                    <Badge variant="outline">
                                                        {sym.period_count}x during period
                                                    </Badge>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}

                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-base flex items-center gap-2">
                                        <Smile className="w-5 h-5 text-indigo-500" />
                                        Mood Patterns
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    {insights.common_period_moods?.length > 0 && (
                                        <div>
                                            <p className="text-sm text-muted-foreground mb-2">During period:</p>
                                            <div className="flex flex-wrap gap-2">
                                                {insights.common_period_moods.map(([mood, count], idx) => (
                                                    <Badge key={idx} className="bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300">
                                                        {mood} ({count}x)
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {insights.common_non_period_moods?.length > 0 && (
                                        <div>
                                            <p className="text-sm text-muted-foreground mb-2">Other days:</p>
                                            <div className="flex flex-wrap gap-2">
                                                {insights.common_non_period_moods.map(([mood, count], idx) => (
                                                    <Badge key={idx} variant="outline">
                                                        {mood} ({count}x)
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            <Card className="md:col-span-2 bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-950/20 dark:to-purple-950/20">
                                <CardContent className="p-6">
                                    <div className="flex items-start gap-4">
                                        <div className="p-3 rounded-xl bg-white dark:bg-gray-900 shadow-sm">
                                            <Sparkles className="w-6 h-6 text-purple-500" />
                                        </div>
                                        <div>
                                            <h3 className="font-semibold text-lg mb-2">Tracking Summary</h3>
                                            <p className="text-muted-foreground">
                                                You've logged <strong>{insights.total_entries}</strong> days, including <strong>{insights.period_days_tracked}</strong> period days. 
                                                Your cycle data is being used in your Medical Timeline to help identify patterns between your menstrual cycle and autoimmune symptoms.
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="history" className="mt-6">
                    {!periods?.periods?.length ? (
                        <Card>
                            <CardContent className="flex flex-col items-center justify-center py-12">
                                <CalendarIcon className="w-12 h-12 text-muted-foreground/50 mb-4" />
                                <h3 className="font-medium text-lg mb-2">No Periods Tracked Yet</h3>
                                <p className="text-muted-foreground text-center max-w-md">
                                    Mark days as period days in the calendar to build your cycle history.
                                </p>
                            </CardContent>
                        </Card>
                    ) : (
                        <div className="space-y-4">
                            {periods.periods.slice().reverse().map((period, idx) => (
                                <Card key={idx}>
                                    <CardContent className="p-4">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium">
                                                    {format(parseISO(period.start_date), 'MMMM d')} - {format(parseISO(period.end_date), 'MMMM d, yyyy')}
                                                </p>
                                                <p className="text-sm text-muted-foreground">
                                                    {period.length} days
                                                </p>
                                            </div>
                                            <Badge variant="outline" className="text-pink-600 border-pink-300">
                                                <Droplets className="w-3 h-3 mr-1" />
                                                Period
                                            </Badge>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}

                            {periods.cycle_lengths?.length > 0 && (
                                <Card className="mt-6">
                                    <CardHeader>
                                        <CardTitle className="text-base">Cycle Length History</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        <div className="flex items-end gap-2 h-24">
                                            {periods.cycle_lengths.map((length, idx) => (
                                                <div key={idx} className="flex-1 flex flex-col items-center">
                                                    <div 
                                                        className="w-full bg-pink-400 rounded-t"
                                                        style={{ height: `${(length / 45) * 100}%` }}
                                                    />
                                                    <span className="text-xs mt-1">{length}d</span>
                                                </div>
                                            ))}
                                        </div>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                    )}
                </TabsContent>
            </Tabs>

            {/* Entry Form Modal */}
            {showEntryForm && (
                <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowEntryForm(false)}>
                    <Card className="w-full max-w-lg max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
                        <CardHeader className="pb-2">
                            <div className="flex items-center justify-between">
                                <CardTitle>
                                    {selectedDate && format(parseISO(selectedDate), 'MMMM d, yyyy')}
                                </CardTitle>
                                <Button variant="ghost" size="icon" onClick={() => setShowEntryForm(false)}>
                                    <X className="w-4 h-4" />
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Period Day Toggle */}
                            <div className="flex items-center justify-between p-4 rounded-xl bg-pink-50 dark:bg-pink-950/30">
                                <div className="flex items-center gap-3">
                                    <Droplets className="w-5 h-5 text-pink-500" />
                                    <div>
                                        <p className="font-medium">Period Day</p>
                                        <p className="text-sm text-muted-foreground">Mark if menstruating</p>
                                    </div>
                                </div>
                                <Switch
                                    checked={isPeriodDay}
                                    onCheckedChange={setIsPeriodDay}
                                    data-testid="period-day-toggle"
                                />
                            </div>

                            {/* Flow Intensity */}
                            {isPeriodDay && (
                                <div className="space-y-2">
                                    <Label>Flow Intensity</Label>
                                    <div className="grid grid-cols-4 gap-2">
                                        {FLOW_INTENSITIES.map(flow => (
                                            <Button
                                                key={flow.value}
                                                variant={flowIntensity === flow.value ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setFlowIntensity(flow.value)}
                                                className={flowIntensity === flow.value ? flow.color : ''}
                                            >
                                                {flow.label}
                                            </Button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Mood */}
                            <div className="space-y-2">
                                <Label>Mood</Label>
                                <div className="grid grid-cols-4 gap-2">
                                    {MOODS.map(m => {
                                        const Icon = m.icon;
                                        return (
                                            <Button
                                                key={m.value}
                                                variant={mood === m.value ? 'default' : 'outline'}
                                                size="sm"
                                                onClick={() => setMood(mood === m.value ? '' : m.value)}
                                                className="flex-col h-auto py-2"
                                            >
                                                <Icon className={`w-4 h-4 ${mood === m.value ? '' : m.color}`} />
                                                <span className="text-xs mt-1">{m.label}</span>
                                            </Button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Energy Level */}
                            <div className="space-y-2">
                                <Label>Energy Level: {energyLevel}/10</Label>
                                <Slider
                                    value={[energyLevel]}
                                    onValueChange={([v]) => setEnergyLevel(v)}
                                    max={10}
                                    min={1}
                                    step={1}
                                />
                            </div>

                            {/* Symptoms */}
                            <div className="space-y-2">
                                <Label>Symptoms</Label>
                                <div className="flex flex-wrap gap-2">
                                    {CYCLE_SYMPTOMS.map(symptom => (
                                        <Badge
                                            key={symptom}
                                            variant={selectedSymptoms.includes(symptom) ? 'default' : 'outline'}
                                            className="cursor-pointer"
                                            onClick={() => toggleSymptom(symptom)}
                                        >
                                            {selectedSymptoms.includes(symptom) && <Check className="w-3 h-3 mr-1" />}
                                            {symptom}
                                        </Badge>
                                    ))}
                                </div>
                            </div>

                            {/* Notes */}
                            <div className="space-y-2">
                                <Label>Notes</Label>
                                <Textarea
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Any additional notes..."
                                    rows={2}
                                />
                            </div>

                            {/* Actions */}
                            <div className="flex gap-2 pt-4">
                                <Button
                                    className="flex-1"
                                    onClick={handleSaveEntry}
                                    disabled={saving}
                                >
                                    {saving ? 'Saving...' : 'Save Entry'}
                                </Button>
                                {entriesByDate[selectedDate] && (
                                    <Button
                                        variant="outline"
                                        onClick={handleDeleteEntry}
                                        className="text-destructive"
                                    >
                                        Delete
                                    </Button>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}
        </div>
    );
}
